import { algoliasearch, type SearchClient } from "algoliasearch";
import type { AppAutocompleteItem } from "@a7y/types";

// Vite injects VITE_* env vars at build time. Cast minimally so this module
// typechecks in @a7y/ui without depending on `vite/client` types.
const env = (import.meta as unknown as { env?: Record<string, string | undefined> })
  .env ?? {};
const APP_ID = env.VITE_ALGOLIA_APP_ID;
const SEARCH_KEY = env.VITE_ALGOLIA_SEARCH_KEY;
const INDEX_NAME = "apps";

let client: SearchClient | null = null;
let warnedMissingEnv = false;

function getClient(): SearchClient | null {
  if (client) return client;
  if (!APP_ID || !SEARCH_KEY) {
    if (!warnedMissingEnv) {
      console.warn(
        "[algolia] VITE_ALGOLIA_APP_ID or VITE_ALGOLIA_SEARCH_KEY not set; " +
          "skipping Algolia search and falling back to live autocomplete endpoint.",
      );
      warnedMissingEnv = true;
    }
    return null;
  }
  client = algoliasearch(APP_ID, SEARCH_KEY);
  return client;
}

interface AppsHit {
  objectID?: string;
  store?: "ios" | "android";
  storeId?: string;
  name?: string;
  developer?: string;
  iconUrl?: string;
  bundleId?: string;
  /**
   * A7Y-1412 — ISO 3166-1 alpha-2 country code (lowercase) for the storefront
   * this record was indexed from. Propagated onto `AppAutocompleteItem` so
   * `apiStoreEnrich` can drive a region-aware lookup. Optional because legacy
   * records ingested before A7Y-1412 lack it.
   */
  region?: string;
}

export async function searchAppsIndex(
  term: string,
  signal?: AbortSignal,
  hitsPerPage = 10,
): Promise<AppAutocompleteItem[]> {
  const trimmed = term.trim();
  if (!trimmed) return [];
  const c = getClient();
  if (!c) return [];
  if (signal?.aborted) return [];

  try {
    const res = await c.searchSingleIndex<AppsHit>({
      indexName: INDEX_NAME,
      searchParams: { query: trimmed, hitsPerPage },
    });
    if (signal?.aborted) return [];

    const items: AppAutocompleteItem[] = [];
    for (const h of res.hits ?? []) {
      if (!h.storeId || !h.store || !h.name) continue;
      if (h.store !== "ios" && h.store !== "android") continue;
      items.push({
        store: h.store,
        storeId: h.storeId,
        name: h.name,
        developer: h.developer,
        iconUrl: h.iconUrl,
        bundleId: h.bundleId,
        region: h.region,
      });
    }
    return items;
  } catch (err) {
    if (signal?.aborted) return [];
    console.warn("[algolia] searchAppsIndex error:", err);
    return [];
  }
}

export function isAlgoliaConfigured(): boolean {
  return Boolean(APP_ID && SEARCH_KEY);
}
